// Shared tone × appearance vocabulary for Button and IconButton.
//
// Two orthogonal axes:
//   - tone:        primary | secondary | destructive | info | success |
//                  warning | neutral
//                  the *meaning* of the action.
//   - appearance:  solid | outline | ghost
//                  the visual emphasis — high (solid), medium (outline),
//                  low (ghost).
//
// The matrix is sparse on purpose: `(neutral, solid)` is excluded because
// "high emphasis without semantic tone" is a contradiction — solid says
// "this matters", and neutral says "this carries no semantic weight".
// All other 20 cells are first-class.
//
// Loader colour is `currentColor`, so each cell already paints its
// spinner correctly: solid cells use the foreground-on-tone variant
// (e.g. white on bg-primary), and outline / ghost cells use the
// tone-on-surface variant (e.g. primary on primary-surface).

const TONE_APPEARANCE_CLASSNAMES = {
    primary: {
        solid: 'tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90',
        outline: 'tw-border tw-border-primary tw-bg-primary-surface tw-text-primary hover:tw-bg-primary hover:tw-text-primary-foreground',
        ghost: 'tw-text-primary hover:tw-bg-primary-surface'
    },
    secondary: {
        solid: 'tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80',
        outline: 'tw-border tw-border-secondary tw-bg-secondary-surface tw-text-secondary hover:tw-bg-secondary hover:tw-text-secondary-foreground',
        ghost: 'tw-text-secondary hover:tw-bg-secondary-surface'
    },
    destructive: {
        solid: 'tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90',
        outline: 'tw-border tw-border-destructive tw-bg-destructive-surface tw-text-destructive hover:tw-bg-destructive hover:tw-text-destructive-foreground',
        ghost: 'tw-text-destructive hover:tw-bg-destructive-surface'
    },
    info: {
        solid: 'tw-bg-info tw-text-info-foreground hover:tw-bg-info/90',
        outline: 'tw-border tw-border-info tw-bg-info-surface tw-text-info hover:tw-bg-info hover:tw-text-info-foreground',
        ghost: 'tw-text-info hover:tw-bg-info-surface'
    },
    success: {
        solid: 'tw-bg-success tw-text-success-foreground hover:tw-bg-success/90',
        outline: 'tw-border tw-border-success tw-bg-success-surface tw-text-success hover:tw-bg-success hover:tw-text-success-foreground',
        ghost: 'tw-text-success hover:tw-bg-success-surface'
    },
    warning: {
        solid: 'tw-bg-warning tw-text-warning-foreground hover:tw-bg-warning/90',
        outline: 'tw-border tw-border-warning tw-bg-warning-surface tw-text-warning hover:tw-bg-warning hover:tw-text-warning-foreground',
        ghost: 'tw-text-warning hover:tw-bg-warning-surface'
    },
    neutral: {
        outline: 'tw-border tw-border-input tw-bg-background tw-text-foreground hover:tw-border-input hover:tw-bg-muted hover:tw-text-foreground focus-visible:tw-border-input disabled:tw-border-border disabled:tw-bg-background disabled:tw-text-muted-foreground disabled:tw-opacity-100',
        ghost: 'tw-text-foreground hover:tw-bg-muted'
    }
};

const SUPPORTED_TONES = Object.keys(TONE_APPEARANCE_CLASSNAMES);

const formatSupportedCombinations = () => (
    SUPPORTED_TONES
        .flatMap(tone => Object.keys(TONE_APPEARANCE_CLASSNAMES[tone]).map(appearance => `${tone}/${appearance}`))
        .join(', ')
);

const resolveButtonToneAppearanceClassName = (tone, appearance) => {
    const className = TONE_APPEARANCE_CLASSNAMES[tone]?.[appearance];

    if (!className) {
        throw new Error(
            `Button tone="${tone}" appearance="${appearance}" is not supported. Valid combinations: ${formatSupportedCombinations()}.`
        );
    }

    return className;
};

export {
    resolveButtonToneAppearanceClassName,
    SUPPORTED_TONES,
    TONE_APPEARANCE_CLASSNAMES
};
